import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { useFranchise } from "@/context/FranchiseContext";

import {
  CustomerInfoStep,
  ServiceSelectionStep,
  SchedulingStep,
  ReviewStep,
  SuccessStep,
  UpsellModal,
  MinimumWarningModal,
  QuoteHeader,
  ProgressSteps,
  NavigationButtons,
  FENCE_TIERS,
  PATIO_SIZES,
  DECK_SIZES,
  CHIMNEY_CONDITIONS,
  SERVICE_ORDER,
  getWindowPricePerUnit,
  getWalkwayPrice,
  getSidingUpcharge,
} from "../components/quote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isInServiceArea } = useFranchise();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [pricingConfig, setPricingConfig] = useState(null);
  
  // Campaign tracking
  const [campaignCode, setCampaignCode] = useState(null);
  
  // Property images state
  const [propertyImages, setPropertyImages] = useState({
    satelliteUrl: null,
    streetviewUrl: null,
    loading: false,
    error: null,
  });
  const [showStreetView, setShowStreetView] = useState(true);

  // Service-specific selections
  const [windowCount, setWindowCount] = useState(10);
  const [fenceLinearFeet, setFenceLinearFeet] = useState(100);
  const [walkwayLinearFeet, setWalkwayLinearFeet] = useState(50);
  const [sidewalkLinearFeet, setSidewalkLinearFeet] = useState(50);
  const [patioSize, setPatioSize] = useState("10x10");
  const [deckSize, setDeckSize] = useState("10x10");
  const [chimneyCondition, setChimneyCondition] = useState("small_moderate");
  const [sidingType, setSidingType] = useState(null);  // Must be selected by user
  
  // Estimate acceptance
  const [estimateAccepted, setEstimateAccepted] = useState(false);
  
  // Maybe Later flag - tracks if user clicked "Maybe Later" instead of accepting
  const [isMaybeLater, setIsMaybeLater] = useState(false);
  
  // Customer notes for special requests
  const [customerNotes, setCustomerNotes] = useState("");
  
  // Upsell modal state
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  
  // Minimum estimate warning modal
  const [showMinimumWarning, setShowMinimumWarning] = useState(false);
  
  // Scrub Club
  const [showScrubClub, setShowScrubClub] = useState(false);

  // Customer info
  const [customerInfo, setCustomerInfo] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    street_address: "",
    city: "",
    state: "IL",
    zip_code: "",
    email_opt_in: true,
    sms_opt_in: false,
  });

  // Lead saved flag
  const [leadSaved, setLeadSaved] = useState(false);

  // Property details - null values require user selection
  const [propertyDetails, setPropertyDetails] = useState({
    square_footage: null,  // Must be selected by user
    stories: null,         // Must be selected by user
    latitude: null,
    longitude: null,
    verified_sqft: null,
    sqft_source: null,
    bedrooms: null,
    bathrooms: null,
    year_built: null,
    siding_type: null,     // Must be selected by user
    window_count: 10,
    fence_linear_feet: 100,
    patio_size: "10x10",
  });

  // Selected services
  const [selectedServices, setSelectedServices] = useState({});

  // Appointment preferences (3 options)
  const [preferredDates, setPreferredDates] = useState([
    { date: null, time_slot: null },
    { date: null, time_slot: null },
    { date: null, time_slot: null },
  ]);

  // Rush/Premium booking request
  const [rushBooking, setRushBooking] = useState(false);

  // Credit code
  const [creditCode, setCreditCode] = useState("");
  const [creditApplied, setCreditApplied] = useState(0);

  // Promo code
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoValidated, setPromoValidated] = useState(false);
  const [promoMessage, setPromoMessage] = useState("");
  const [promoDiscountType, setPromoDiscountType] = useState(null); // "fixed" or "percentage"
  const [promoDiscountValue, setPromoDiscountValue] = useState(0); // Raw value (e.g., 50 for $50 or 10 for 10%)

  // Calculated prices
  const [calculatedPrices, setCalculatedPrices] = useState({});
  const [subtotal, setSubtotal] = useState(0);
  const [bundleDiscount, setBundleDiscount] = useState(0);
  const [total, setTotal] = useState(0);

  // Helper function to build full address
  const getFullAddress = useCallback(() => {
    return `${customerInfo.street_address}, ${customerInfo.city}, ${customerInfo.state} ${customerInfo.zip_code}`.trim();
  }, [customerInfo]);

  // Capture campaign code from URL or sessionStorage
  useEffect(() => {
    const urlCampaign = searchParams.get("campaign");
    const storedCampaign = sessionStorage.getItem("campaign_code");
    const code = urlCampaign || storedCampaign;
    if (code) {
      setCampaignCode(code);
      // Campaign tracking is stored for quote attribution
    }
  }, [searchParams, setCampaignCode]);

  // Fetch pricing config
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API}/pricing`);
        setPricingConfig(res.data);
      } catch (err) {
        toast.error("Failed to load pricing configuration");
      }
    };
    fetchPricing();
  }, []);

  // Calculate prices when services or property changes
  const calculatePrices = useCallback(() => {
    if (!pricingConfig) return;

    const prices = {};
    const selectedCount = Object.values(selectedServices).filter(Boolean).length;
    const sqft = propertyDetails.verified_sqft || propertyDetails.square_footage || 2000; // Default for calculation

    const getPriceFromTiers = (tiers, value = sqft) => {
      if (!tiers || tiers.length === 0) return 0;
      for (const tier of tiers) {
        if (value >= tier.min_sqft && (tier.max_sqft === null || value <= tier.max_sqft)) {
          return tier.price;
        }
      }
      return tiers[0]?.price || 0;
    };

    const getBundleDiscount = () => {
      if (!pricingConfig.bundle_discounts) return 0;
      let discount = 0;
      for (const bd of pricingConfig.bundle_discounts) {
        if (selectedCount >= bd.min_services) {
          discount = bd.discount_percent;
        }
      }
      return discount;
    };

    const bundleDiscountPercent = getBundleDiscount();

    pricingConfig.services.forEach((service) => {
      let basePrice = 0;
      let requiresQuote = false;
      
      switch (service.pricing_type) {
        case 'per_window':
          const pricePerWindow = getWindowPricePerUnit(windowCount);
          basePrice = windowCount * pricePerWindow;
          break;
          
        case 'linear_ft_tier':
          if (service.service_id === 'fence') {
            const fenceTier = FENCE_TIERS.find(t => fenceLinearFeet >= t.min && fenceLinearFeet <= t.max);
            basePrice = fenceTier?.price || 0;
          } else {
            basePrice = getPriceFromTiers(service.tiers, sqft);
          }
          break;
          
        case 'linear_ft_input':
          // Walkways and Sidewalk - user enters linear feet
          if (service.service_id === 'entries') {
            basePrice = getWalkwayPrice(walkwayLinearFeet);
          } else if (service.service_id === 'sidewalk') {
            basePrice = getWalkwayPrice(sidewalkLinearFeet);
          }
          break;
          
        case 'size_based':
          if (service.service_id === 'patio') {
            const patioOption = PATIO_SIZES.find(p => p.value === patioSize);
            basePrice = patioOption?.price || 0;
            if (patioSize === 'larger') requiresQuote = true;
          } else if (service.service_id === 'deck') {
            const deckOption = DECK_SIZES.find(d => d.value === deckSize);
            basePrice = deckOption?.price || 0;
            if (deckSize === 'larger') requiresQuote = true;
          }
          break;
          
        case 'condition_based':
          const chimneyOption = CHIMNEY_CONDITIONS.find(c => c.value === chimneyCondition);
          basePrice = chimneyOption?.price || 0;
          if (chimneyCondition === 'other') requiresQuote = true;
          break;
          
        case 'sqft_tier':
        default:
          basePrice = getPriceFromTiers(service.tiers);
          // Add siding upcharge based on house size
          if (service.service_id === 'siding') {
            const sidingUpcharge = getSidingUpcharge(sidingType || 'standard', sqft);
            basePrice += sidingUpcharge;
          }
          break;
      }
      
      if (basePrice === 0 && !requiresQuote && service.pricing_type === 'sqft_tier') {
        if (sqft > 5000) requiresQuote = true;
      }

      if (requiresQuote || basePrice === 0) {
        prices[service.service_id] = {
          original: 0,
          discounted: 0,
          savings: 0,
          requiresQuote: true,
        };
        return;
      }

      const storyMultiplier = (propertyDetails.stories || 1) > 1
        ? Math.pow(service.story_multiplier || 1, (propertyDetails.stories || 1) - 1)
        : 1;

      let price = basePrice * storyMultiplier;

      if (bundleDiscountPercent > 0 && selectedServices[service.service_id]) {
        const discountedPrice = price * (1 - bundleDiscountPercent / 100);
        prices[service.service_id] = {
          original: Math.round(price),
          discounted: Math.round(discountedPrice),
          savings: Math.round(price - discountedPrice),
          requiresQuote: false,
        };
      } else {
        prices[service.service_id] = {
          original: Math.round(price),
          discounted: Math.round(price),
          savings: 0,
          requiresQuote: false,
        };
      }
    });

    setCalculatedPrices(prices);

    // Calculate totals
    let fullSubtotal = 0;
    let discountedTotal = 0;

    Object.entries(selectedServices)
      .filter(([_, selected]) => selected)
      .forEach(([serviceId]) => {
        const price = prices[serviceId];
        if (price && !price.requiresQuote) {
          fullSubtotal += price.original;
          discountedTotal += price.discounted;
        }
      });

    setSubtotal(Math.round(fullSubtotal));
    setBundleDiscount(Math.round(fullSubtotal - discountedTotal));
    
    // Recalculate promo discount for percentage codes based on actual subtotal
    let actualPromoDiscount = promoDiscount;
    if (promoValidated && promoDiscountType === 'percentage' && fullSubtotal > 0) {
      actualPromoDiscount = Math.round((fullSubtotal * promoDiscountValue) / 100);
      // Update the promo discount state with recalculated value
      if (actualPromoDiscount !== promoDiscount) {
        setPromoDiscount(actualPromoDiscount);
      }
    }
    
    // Apply promo discount to total (after bundle discount)
    const finalTotal = Math.max(0, Math.round(discountedTotal) - actualPromoDiscount);
    setTotal(finalTotal);
    
    // Calculate Scrub Club eligibility
    if (selectedServices['siding'] && selectedServices['driveway']) {
      setShowScrubClub(true);
    } else {
      setShowScrubClub(false);
    }
  }, [pricingConfig, selectedServices, propertyDetails, windowCount, fenceLinearFeet, walkwayLinearFeet, sidewalkLinearFeet, patioSize, deckSize, chimneyCondition, sidingType, promoDiscount, promoValidated, promoDiscountType, promoDiscountValue]);

  useEffect(() => {
    calculatePrices();
  }, [calculatePrices]);

  const toggleService = (serviceId) => {
    setSelectedServices((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId],
    }));
  };

  const updatePreferredDate = (index, field, value) => {
    setPreferredDates(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Fetch property images
  const fetchPropertyImages = async (lat, lng) => {
    setPropertyImages(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await axios.get(`${API}/property/images`, {
        params: { lat, lng, address: getFullAddress() }
      });
      
      if (response.data.success) {
        setPropertyImages({
          satelliteUrl: response.data.satellite_url,
          streetviewUrl: response.data.streetview_url,
          loading: false,
          error: null,
        });
      } else {
        setPropertyImages(prev => ({
          ...prev,
          loading: false,
          error: response.data.message || "Could not load property images"
        }));
      }
    } catch (err) {
      // Property images are non-critical - silently fail
      setPropertyImages(prev => ({
        ...prev,
        loading: false,
        error: "Failed to load property images"
      }));
    }
  };

  // Save lead to CRM
  const saveLead = async () => {
    if (leadSaved) return;
    
    try {
      await axios.post(`${API}/leads`, {
        name: `${customerInfo.first_name} ${customerInfo.last_name}`.trim(),
        email: customerInfo.email,
        phone: customerInfo.phone,
        address: getFullAddress(),
        email_opt_in: customerInfo.email_opt_in,
        sms_opt_in: customerInfo.sms_opt_in,
        source: { source: "website", landing_page: "/quote" }
      });
      setLeadSaved(true);
    } catch {
      // Lead save is non-blocking - don't interrupt quote flow
    }
  };

  // Validate address fields
  const validateAddressFields = () => {
    const issues = [];
    if (!customerInfo.street_address || customerInfo.street_address.length < 5) {
      issues.push("Street address is required");
    } else if (!/^\d+/.test(customerInfo.street_address.trim())) {
      issues.push("Street address should start with a number");
    }
    if (!customerInfo.city || customerInfo.city.length < 2) {
      issues.push("City is required");
    }
    if (!customerInfo.state) {
      issues.push("State is required");
    }
    if (!customerInfo.zip_code) {
      issues.push("ZIP code is required");
    } else if (!/^\d{5}(-\d{4})?$/.test(customerInfo.zip_code)) {
      issues.push("ZIP code must be 5 digits");
    }
    return { valid: issues.length === 0, issues };
  };

  const validateStep1 = () => {
    if (!customerInfo.first_name || !customerInfo.last_name || !customerInfo.email) {
      toast.error("Please fill in your first name, last name, and email");
      return false;
    }
    // Phone is required only if SMS opt-in is checked
    if (customerInfo.sms_opt_in && !customerInfo.phone) {
      toast.error("Phone number is required when opting in for SMS notifications");
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(customerInfo.email)) {
      toast.error("Please enter a valid email");
      return false;
    }
    const addressCheck = validateAddressFields();
    if (!addressCheck.valid) {
      toast.error(addressCheck.issues[0] || "Please complete your address");
      return false;
    }
    // Check service area
    if (!isInServiceArea(customerInfo.zip_code)) {
      toast.error("Sorry, this address is outside our service area. We currently serve DuPage County and surrounding areas in Illinois.");
      return false;
    }
    // Validate required property details
    if (!propertyDetails.square_footage) {
      toast.error("Please select your house size (square footage)");
      return false;
    }
    if (!propertyDetails.stories) {
      toast.error("Please select the number of stories");
      return false;
    }
    if (!sidingType) {
      toast.error("Please select your siding material");
      return false;
    }
    saveLead();
    return true;
  };

  const validateStep2 = () => {
    if (Object.values(selectedServices).filter(Boolean).length === 0) {
      toast.error("Please select at least one service");
      return false;
    }
    return true;
  };

  // Get selected service count
  const getSelectedServiceCount = () => {
    return Object.values(selectedServices).filter(Boolean).length;
  };

  // Get suggested services for upsell
  const getUpsellSuggestions = () => {
    if (!pricingConfig) return [];
    const selectedCount = getSelectedServiceCount();
    // Max discount is 10% for 3+ services
    const discountPercent = selectedCount < 2 ? 5 : 10;
    
    // Define priority order based on what's selected
    // Always prioritize windows if not selected, then driveway, gutters
    let priorityOrder;
    if (!selectedServices['windows']) {
      // Windows not selected - make it top priority
      priorityOrder = ['windows', 'driveway', 'gutters', 'siding', 'entries', 'sidewalk', 'patio', 'deck', 'fence', 'roof', 'chimney'];
    } else if (selectedServices['siding']) {
      // Siding selected, windows already selected - prioritize driveway, gutters
      priorityOrder = ['driveway', 'gutters', 'entries', 'sidewalk', 'patio', 'deck', 'fence', 'roof', 'chimney'];
    } else {
      // Default priority following service order
      priorityOrder = SERVICE_ORDER;
    }
    
    // Get unselected services with valid prices
    const unselectedServices = pricingConfig.services
      .filter((s) => !selectedServices[s.service_id] && calculatedPrices[s.service_id]?.original > 0);
    
    // Sort by priority order
    const sortedServices = unselectedServices.sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a.service_id);
      const bIndex = priorityOrder.indexOf(b.service_id);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });
    
    // Always show 2-3 suggestions (or fewer if not many left)
    const suggestionCount = Math.min(3, sortedServices.length);
    
    return sortedServices
      .slice(0, suggestionCount)
      .map((service) => {
        const originalPrice = calculatedPrices[service.service_id]?.original || 0;
        const bundlePrice = Math.round(originalPrice * (1 - discountPercent / 100));
        return {
          ...service,
          originalPrice,
          bundlePrice,
          savings: originalPrice - bundlePrice,
        };
      });
  };

  // Proceed to next step (bypassing upsell)
  const proceedToNextStep = async () => {
    setShowUpsellModal(false);
    
    // Check minimum estimate when leaving Step 2 (Services)
    if (step === 2) {
      const minimumEstimate = pricingConfig?.minimum_estimate || 175;
      if (total < minimumEstimate) {
        setShowMinimumWarning(true);
        return;
      }
    }
    
    // Fetch property images when moving from step 1 to step 2
    if (step === 1) {
      if (!propertyDetails.latitude || !propertyDetails.longitude) {
        try {
          const res = await axios.post(`${API}/property/lookup`, {
            address: getFullAddress(),
            zipcode: customerInfo.zip_code
          });
          
          if (res.data.latitude && res.data.longitude) {
            setPropertyDetails(prev => ({
              ...prev,
              latitude: res.data.latitude,
              longitude: res.data.longitude,
            }));
            fetchPropertyImages(res.data.latitude, res.data.longitude);
          }
        } catch {
          // Geocoding failed - property images won't load but quote can continue
        }
      } else {
        fetchPropertyImages(propertyDetails.latitude, propertyDetails.longitude);
      }
    }
    
    setStep((prev) => Math.min(prev + 1, 5));
    window.scrollTo(0, 0);
  };

  const validateStep3 = () => {
    // If rush booking is selected, date selection is optional
    if (rushBooking) {
      return true;
    }
    
    const filledDates = preferredDates.filter(d => d.date && d.time_slot);
    if (filledDates.length < 3) {
      toast.error("Please select 3 different preferred dates and times, or select 'Need Service ASAP'");
      return false;
    }
    return true;
  };

  const nextStep = async () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    
    // Show upsell modal for any number of services on step 2 (if there are still services to add)
    if (step === 2) {
      const selectedCount = getSelectedServiceCount();
      const totalServices = pricingConfig?.services?.length || 11;
      // Show upsell if there are still services available to add
      if (selectedCount >= 1 && selectedCount < totalServices) {
        setShowUpsellModal(true);
        return;
      }
    }
    
    await proceedToNextStep();
  };

  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmitQuote = async () => {
    setSubmitting(true);
    try {
      const selectedServicesList = pricingConfig.services
        .filter((s) => selectedServices[s.service_id])
        .map((s) => ({
          service_id: s.service_id,
          service_name: s.service_name,
          calculated_price: calculatedPrices[s.service_id]?.discounted || 0,
        }));

      const formattedDates = preferredDates
        .filter(d => d.date && d.time_slot)
        .map(d => ({
          date: format(d.date, 'yyyy-MM-dd'),
          time_slot: d.time_slot
        }));

      const customerData = {
        name: `${customerInfo.first_name} ${customerInfo.last_name}`.trim(),
        first_name: customerInfo.first_name,
        last_name: customerInfo.last_name,
        email: customerInfo.email,
        phone: customerInfo.phone,
        address: getFullAddress(),
        email_opt_in: customerInfo.email_opt_in,
        sms_opt_in: customerInfo.sms_opt_in,
      };

      const propertyData = {
        ...propertyDetails,
        siding_type: sidingType,
        window_count: windowCount,
        fence_linear_feet: fenceLinearFeet,
        patio_size: patioSize,
      };

      const quoteData = {
        customer: customerData,
        property_details: propertyData,
        selected_services: selectedServicesList,
        subtotal,
        bundle_discount: bundleDiscount,
        total: total + promoDiscount, // Send original total, promo applied on backend
        // Don't send dates if rush booking is selected
        preferred_dates: rushBooking ? null : (formattedDates.length > 0 ? formattedDates : null),
        notes: customerNotes || null,
        rush_booking: rushBooking,
        credit_code: creditApplied > 0 ? creditCode : null,
        campaign_code: campaignCode || null,  // Track campaign source
        promo_code: promoValidated ? promoCode : null,
        promo_discount: promoDiscount,
      };

      await axios.post(`${API}/quotes`, quoteData);
      toast.success("Quote submitted successfully!");
      // Redirect to external thank you page for tracking
      setSubmitting(false);
      window.location.href = "https://rollingsudsdupage.com/thank-you/";
      return; // Ensure we exit after redirect
    } catch (err) {
      toast.error("Failed to submit quote");
      setSubmitting(false);
    }
  };

  // Handle "Maybe Later" - saves quote info without sending email
  const handleMaybeLater = async () => {
    setSubmitting(true);
    try {
      const selectedServicesList = pricingConfig.services
        .filter((s) => selectedServices[s.service_id])
        .map((s) => ({
          service_id: s.service_id,
          service_name: s.service_name,
          calculated_price: calculatedPrices[s.service_id]?.discounted || 0,
        }));

      const formattedDates = preferredDates
        .filter(d => d.date && d.time_slot)
        .map(d => ({
          date: format(d.date, 'yyyy-MM-dd'),
          time_slot: d.time_slot
        }));

      const customerData = {
        name: `${customerInfo.first_name} ${customerInfo.last_name}`.trim(),
        first_name: customerInfo.first_name,
        last_name: customerInfo.last_name,
        email: customerInfo.email,
        phone: customerInfo.phone,
        address: getFullAddress(),
        email_opt_in: customerInfo.email_opt_in,
        sms_opt_in: customerInfo.sms_opt_in,
      };

      const propertyData = {
        ...propertyDetails,
        siding_type: sidingType,
        window_count: windowCount,
        fence_linear_feet: fenceLinearFeet,
        patio_size: patioSize,
      };

      const quoteData = {
        customer: customerData,
        property_details: propertyData,
        selected_services: selectedServicesList,
        subtotal,
        bundle_discount: bundleDiscount,
        total: total + promoDiscount, // Send original total, promo applied on backend
        preferred_dates: rushBooking ? null : (formattedDates.length > 0 ? formattedDates : null),
        notes: customerNotes || null,
        rush_booking: rushBooking,
        credit_code: null,  // No credit for maybe later
        campaign_code: campaignCode || null,
        promo_code: promoValidated ? promoCode : null,
        promo_discount: promoDiscount,
      };

      await axios.post(`${API}/quotes/maybe-later`, quoteData);
      
      // Redirect IMMEDIATELY to external thank you page - do not show internal success page
      window.location.href = "https://rollingsudsdupage.com/thank-you/";
      // Note: Code below this line won't execute due to redirect
    } catch (err) {
      toast.error("Failed to save information");
      setSubmitting(false);
    }
  };

  // Handler for upsell service addition
  const handleUpsellAdd = (service) => {
    toggleService(service.service_id);
    setShowUpsellModal(false);
    toast.success(`${service.service_name} added! Bundle discount applied.`);
  };

  // Handler for minimum warning service addition
  const handleMinimumAdd = (service) => {
    toggleService(service.service_id);
    setShowMinimumWarning(false);
    toast.success(`${service.service_name} added!`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Modals */}
      <UpsellModal
        open={showUpsellModal}
        onOpenChange={setShowUpsellModal}
        selectedServiceCount={getSelectedServiceCount()}
        upsellSuggestions={getUpsellSuggestions()}
        onAddService={handleUpsellAdd}
        onSkip={proceedToNextStep}
      />

      <MinimumWarningModal
        open={showMinimumWarning}
        onOpenChange={setShowMinimumWarning}
        minimumEstimate={pricingConfig?.minimum_estimate || 175}
        currentTotal={total}
        upsellSuggestions={getUpsellSuggestions()}
        onAddService={handleMinimumAdd}
      />

      {/* Header */}
      <QuoteHeader />

      {/* Progress Steps */}
      <ProgressSteps currentStep={step} />

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {step === 1 && (
          <CustomerInfoStep
            customerInfo={customerInfo}
            setCustomerInfo={setCustomerInfo}
            propertyDetails={propertyDetails}
            setPropertyDetails={setPropertyDetails}
            sidingType={sidingType}
            setSidingType={setSidingType}
            getFullAddress={getFullAddress}
            onPropertyImagesLoad={fetchPropertyImages}
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            promoDiscount={promoDiscount}
            setPromoDiscount={setPromoDiscount}
            promoValidated={promoValidated}
            setPromoValidated={setPromoValidated}
            promoMessage={promoMessage}
            setPromoMessage={setPromoMessage}
            promoDiscountType={promoDiscountType}
            setPromoDiscountType={setPromoDiscountType}
            promoDiscountValue={promoDiscountValue}
            setPromoDiscountValue={setPromoDiscountValue}
            subtotal={subtotal}
          />
        )}

        {step === 2 && (
          <ServiceSelectionStep
            pricingConfig={pricingConfig}
            selectedServices={selectedServices}
            toggleService={toggleService}
            calculatedPrices={calculatedPrices}
            propertyImages={propertyImages}
            showStreetView={showStreetView}
            setShowStreetView={setShowStreetView}
            getFullAddress={getFullAddress}
            propertyDetails={propertyDetails}
            windowCount={windowCount}
            setWindowCount={setWindowCount}
            fenceLinearFeet={fenceLinearFeet}
            setFenceLinearFeet={setFenceLinearFeet}
            walkwayLinearFeet={walkwayLinearFeet}
            setWalkwayLinearFeet={setWalkwayLinearFeet}
            sidewalkLinearFeet={sidewalkLinearFeet}
            setSidewalkLinearFeet={setSidewalkLinearFeet}
            patioSize={patioSize}
            setPatioSize={setPatioSize}
            deckSize={deckSize}
            setDeckSize={setDeckSize}
            chimneyCondition={chimneyCondition}
            setChimneyCondition={setChimneyCondition}
            sidingType={sidingType}
          />
        )}

        {step === 3 && (
          <SchedulingStep
            preferredDates={preferredDates}
            updatePreferredDate={updatePreferredDate}
            rushBooking={rushBooking}
            setRushBooking={setRushBooking}
          />
        )}

        {step === 4 && (
          <ReviewStep
            customerInfo={customerInfo}
            propertyDetails={propertyDetails}
            preferredDates={preferredDates}
            pricingConfig={pricingConfig}
            selectedServices={selectedServices}
            calculatedPrices={calculatedPrices}
            subtotal={subtotal}
            bundleDiscount={bundleDiscount}
            total={total}
            showScrubClub={showScrubClub}
            estimateAccepted={estimateAccepted}
            setEstimateAccepted={setEstimateAccepted}
            getFullAddress={getFullAddress}
            customerNotes={customerNotes}
            setCustomerNotes={setCustomerNotes}
            rushBooking={rushBooking}
            creditCode={creditCode}
            setCreditCode={setCreditCode}
            creditApplied={creditApplied}
            setCreditApplied={setCreditApplied}
            promoCode={promoCode}
            promoDiscount={promoDiscount}
          />
        )}

        {step === 5 && (
          <SuccessStep
            customerInfo={customerInfo}
            total={total}
            bundleDiscount={bundleDiscount}
            isMaybeLater={isMaybeLater}
          />
        )}

        {/* Navigation Buttons */}
        <NavigationButtons
          currentStep={step}
          onPrevStep={prevStep}
          onNextStep={nextStep}
          onSubmit={handleSubmitQuote}
          onMaybeLater={handleMaybeLater}
          submitting={submitting}
          estimateAccepted={estimateAccepted}
        />
      </main>
    </div>
  );
}
